import { useState } from 'react';

import { Avatar } from '../components/Avatar.js';
import { AvatarStack } from '../components/AvatarStack.js';
import { Icon } from '../components/Icon.js';
import type { ParticipantId } from '../fixtures/domain.js';
import type { Guest } from './draft.js';
import styles from './GuestList.module.css';

/** Faces before the rest collapse into a "+N", as everywhere else in the app. */
const FACES = 5;

/**
 * Up to this many guests are simply listed.
 *
 * A stack, a chevron and a fold are three pieces of machinery for hiding three
 * names, and the names are shorter than the machinery. Past it the roll would
 * take the column, so it folds.
 *
 * Which means a Chat Day for two opens with both of them on screen — the fold is
 * for the twenty-four case it was built for, not a default that hides everything
 * on principle.
 */
const FEW = 3;

interface GuestListProps {
  invitees: Guest[];
  /** Already on the saved event being edited: listed as invited, never removable. */
  invited?: { id: string; name: string }[];
  /** Marked when Save was pressed with nobody invited. See `missing`. */
  gap: boolean;
  onRemove: (id: ParticipantId) => void;
}

/**
 * Who is coming, as a field of the form.
 *
 * It sits in the left column with the title and the times rather than in the
 * picker beside it, and the split is the point: this column is the thing being
 * made, and the guest list is part of that thing — the picker is only the drawer
 * you take people out of. Keeping the two together meant a pane that was both a
 * record and a tool, and they competed for the same height (adding the tenth
 * person left barely a slot to find the eleventh in).
 */
export function GuestList({ invitees, invited = [], gap, onRemove }: GuestListProps) {
  const [listing, setListing] = useState(false);
  const everyone = [...invited, ...invitees];
  // Few enough to just show. The stack and the chevron are for the case where
  // showing them would take the column.
  const rolled = everyone.length <= FEW || listing;

  const heading = (
    <>Guests{everyone.length > 0 && <span className={styles.count}> ({everyone.length})</span>}</>
  );

  return (
    <section className={styles.section}>
      {/* The label doubles as the fold only past `FEW`. With nobody invited there
          is nothing to open, and with three there is nothing worth hiding —
          either way it stays a plain label rather than becoming a button that
          does nothing anyone wanted. */}
      {everyone.length > FEW ? (
        <button
          type="button"
          className={styles.fold}
          aria-expanded={listing}
          onClick={() => setListing((open) => !open)}
        >
          <span className={gap ? styles.labelGap : styles.label}>{heading}</span>
          <Icon name={listing ? 'chevronUp' : 'chevronDown'} size={16} />
        </button>
      ) : (
        <p
          className={gap ? styles.labelGap : styles.label}
          data-gap={gap || undefined}
          tabIndex={-1}
        >
          {heading}
        </p>
      )}

      {everyone.length === 0 ? (
        <p className={styles.empty}>Nobody yet. Add people from the panel beside this.</p>
      ) : rolled ? (
        <ul className={styles.roll}>
          {invited.map((person) => (
            <li key={person.id} className={styles.guest}>
              <Avatar name={person.name} size={28} />
              <span className={styles.guestName}>{person.name}</span>
              <span className={styles.invited}>
                <Icon name="check" size={14} />
                Invited
              </span>
            </li>
          ))}
          {invitees.map((person) => (
            <li key={person.id} className={styles.guest}>
              <Avatar name={person.name} src={person.avatarUrl} locked={person.locked} size={28} />
              <span className={styles.guestName}>{person.name}</span>
              <button
                type="button"
                className={styles.drop}
                aria-label={`Remove ${person.name}`}
                onClick={() => onRemove(person.id)}
              >
                <Icon name="close" size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className={styles.stack}>
          <AvatarStack people={everyone} max={FACES} size={32} onMore={() => setListing(true)} />
        </div>
      )}
    </section>
  );
}
